import React, { ComponentProps, useCallback, useMemo } from 'react';
import Workspace from '../../components/Workspace/Workspace';
import { PurchaseTransaction } from '../../graphql/entities';
import { FinancialTransactionModalFormatter } from '../../components/modals/FinancialTransactionModal';
import PurchaseTransactionNoteModalFormatter from './PurchaseTransactionNoteModalFormatter';
import Formatters from '../../components/grid-addons/formatters';
import fieldMetaData from '../../graphql/schema/fieldMetaData';
import { ALLOW_VIEW } from '../../utils/OperationPermissions';
import getAuditModalProps from '../../utils/getAuditModalProps';
import { useWorkspace } from '../../graphql/hooks/useWorkspace';
import { CostViewPermission, Direction, IPurchaseTransaction } from '../../graphql/schema/p4types';
import { IWorkspaceColumnOverride } from '../../@types/workspaceTypes';
import { Paths } from '../../@types/keyOfNested';
import { WorkspaceActionButtons } from '../../components/Workspace/WorkspaceActionButtons';
import { usePurchaseTransactionWorkspaceActions } from '../../actions/purchasing/PurchaseTransactionWorkspaceAction';
import { ModalFormatter } from '../../components/grid-addons/formatters/ModalFormatter';
import { useFullUserDetails } from '../../utils/hooks';
import { IEditableDataGridColumnDef } from '../../@types/editableDataGridTypes';
import PurchaseTransactionBalanceCards from './PurchaseTransactionWorkspaceBalanceCards';

const LazyFinancialAuditModal = React.lazy(() => import('../../components/modals/FinancialAuditModal'));
const LazySupplierForm = React.lazy(() => import('../../views/supplier/SupplierForm'));

const { workspace, type, title } = PurchaseTransaction;

const PurchaseTransactionWorkspaceActions = (
  props: Omit<ComponentProps<typeof WorkspaceActionButtons>, 'actionButtons'>
) => {
  const [actions, modals] = usePurchaseTransactionWorkspaceActions();
  return (
    <>
      {modals}
      <WorkspaceActionButtons {...props} actionButtons={actions} />
    </>
  );
};

const PurchaseTransactionWorkspace = (props) => {
  const fullUserDetails = useFullUserDetails();

  const fieldNames: Array<Paths<IPurchaseTransaction>> = [
    'transactionNumber',
    'documentDate',
    'documentType',
    'documentNumber',
    'documentReference',
    'supplier.reference',
    'paymentDueDate',
    'held',
    'note',
  ];
  if (fullUserDetails?.companyUser?.costViewPermission === CostViewPermission.All) {
    fieldNames.push('amount', 'outstandingAmount');
  }

  const columnOverrides = useMemo<IWorkspaceColumnOverride>(
    () => ({
      transactionNumber: {
        name: 'Transaction No',
        formatter: ModalFormatter,
        options: {
          modalContent: LazyFinancialAuditModal,
          entityId: 'sourceId',
          modalProps: getAuditModalProps,
        },
        width: 120,
      },
      documentDate: {
        name: 'Date',
        key: 'documentDate',
        formatter: Formatters.DateFormatter,
        width: 110,
      },
      documentType: {
        name: 'Source Type',
        key: 'documentType',
        width: 140,
        options: fieldMetaData.PurchaseDocumentType,
        formatter: Formatters.EnumFormatter,
      },
      amount: {
        name: 'Amount',
        formatter: Formatters.NumericFormatter,
        width: 120,
        options: {
          format: { decimals: 2 },
        },
      },
      outstandingAmount: {
        name: 'Outstanding',
        formatter: Formatters.NumericFormatter,
        width: 120,
        options: {
          format: { decimals: 2 },
        },
      },
      paymentDueDate: {
        name: 'Due Date',
        key: 'paymentDueDate',
        formatter: Formatters.DateFormatter,
        width: 110,
      },
      createdAt: {
        name: 'Created At',
        key: 'createdAt',
        formatter: Formatters.DateTimeFormatter,
        width: 140,
      },
      documentNumber: {
        name: 'Source No',
        formatter: FinancialTransactionModalFormatter,
        width: 125,
        options: {
          entityId: 'sourceId',
        },
      },
      'supplier.reference': {
        name: 'Supplier',
        formatter: ModalFormatter,
        width: 125,
        options: {
          modalContent: LazySupplierForm,
          modalProps: { forceReadOnly: true },
          entityId: 'supplier.id',
        },
      },
      held: {
        name: 'Held',
        formatter: Formatters.BooleanFormatter,
        width: 60,
      },
      note: {
        name: 'Note',
        formatter: PurchaseTransactionNoteModalFormatter,
      },
      baseVatAmount: {
        name: 'Base VAT Amount',
      },
      baseVatAmountLessSettlementDiscount: {
        name: 'Base VAT Amount Less Settlement Discount',
      },
      baseVatDiscountAmount: {
        name: 'Base VAT Discount Amount',
      },
      vatAmount: {
        name: 'VAT Amount',
      },
      vatAmountLessSettlementDiscount: {
        name: 'VAT Amount Less Settlement Discount',
      },
      vatDiscountAmount: {
        name: 'VAT Discount Amount',
      },
      vatCreditExpected: {
        name: 'VAT Credit Expected',
      },
    }),
    []
  );

  const workspaceProps = useWorkspace<IPurchaseTransaction>(props, PurchaseTransaction, {
    columns: fieldNames,
    columnOverrides: columnOverrides,
    initialSortColumn: 'transactionNumber',
    initialSortDirection: Direction.Desc,
  });

  const availableColumnsFilter = useCallback(
    (column: IEditableDataGridColumnDef): boolean => {
      if (!column?.fieldName) {
        return false;
      }
      return !(
        fullUserDetails?.companyUser?.costViewPermission !== CostViewPermission.All &&
        (column.fieldName === 'amount' || column.fieldName === 'outstandingAmount')
      );
    },
    [fullUserDetails?.companyUser?.costViewPermission]
  );

  return (
    <Workspace
      entityType={type}
      title={title}
      defaultQuery={workspace}
      permittedOperations={ALLOW_VIEW}
      availableColumnsFilter={availableColumnsFilter}
      {...workspaceProps}
      {...props}
      entity={PurchaseTransaction}
      workspaceActionButtonsHOC={PurchaseTransactionWorkspaceActions}
      additionalCards={<PurchaseTransactionBalanceCards />}
    />
  );
};

export default PurchaseTransactionWorkspace;
